/**
 * 🔥 Firebase Config + Initialization
 *
 * Single source of truth for Firebase services.
 * يهيئ Auth, Firestore, Storage مرة واحدة فقط.
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import {
  initializeFirestore,
  connectFirestoreEmulator,
  persistentLocalCache,
  persistentMultipleTabManager,
  type Firestore,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';

import { env } from '@config/env';

const firebaseConfig = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID,
};

// ─── Initialize App (singleton) ───
const app: FirebaseApp = initializeApp(firebaseConfig);

// ─── Auth ───
const auth: Auth = getAuth(app);

// 🔒 الـ tokens في memory + IndexedDB (مش localStorage = أكثر أماناً ضد XSS)
setPersistence(auth, browserLocalPersistence).catch((err) => {
  console.error('Firebase Auth persistence failed:', err);
});

// ─── Firestore (مع offline persistence + multi-tab) ───
const db: Firestore = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});

// ─── Storage ───
const storage: FirebaseStorage = getStorage(app);

// ─── Emulator Setup (للـ dev) ───
if (env.VITE_USE_FIREBASE_EMULATOR && env.VITE_APP_ENV === 'development') {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    console.log('🔧 Firebase Emulators connected');
  } catch (err) {
    console.warn('Failed to connect emulators:', err);
  }
}

export { app, auth, db, storage };
